import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { tenantsApi, type TenantSummary, type LifecycleState, ApiError } from '../api/client';
import { LifecycleBadge, BillingBadge } from '../components/StatusBadge';

const FILTERS: Array<{ value: LifecycleState | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'OFFBOARDED', label: 'Offboarded' },
];

export function TenantsListPage() {
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [filter, setFilter] = useState<LifecycleState | 'ALL'>('ALL');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTenants(null);
    setError(null);
    (async () => {
      try {
        const result = await tenantsApi.list(filter === 'ALL' ? undefined : filter);
        if (!cancelled) setTenants(result.tenants);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? err.body.message ?? err.body.error ?? 'Failed to load'
            : 'Failed to load',
        );
      }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Tenants</h1>
          <p className="text-sm text-ink-muted">All clinics on the platform.</p>
        </div>
        <Link
          to="/tenants/new"
          className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-ink hover:bg-primary/90 transition"
        >
          New tenant
        </Link>
      </div>

      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-md px-3 py-1.5 text-xs uppercase tracking-wider transition ${
              filter === f.value
                ? 'bg-surface-3 text-ink'
                : 'bg-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <p role="alert" className="text-sm text-bad">{error}</p>}

      {tenants === null && !error && (
        <p className="text-sm text-ink-muted">Loading…</p>
      )}

      {tenants && tenants.length === 0 && (
        <div className="rounded-2xl bg-surface-2 p-8 text-center space-y-2">
          <p className="text-ink">No tenants yet.</p>
          <p className="text-sm text-ink-muted">Create the first tenant to get started.</p>
        </div>
      )}

      {tenants && tenants.length > 0 && (
        <div className="rounded-2xl bg-surface-2 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-3 text-ink-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-medium px-4 py-3">Display name</th>
                <th className="text-left font-medium px-4 py-3">Slug</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3">Billing</th>
                <th className="text-left font-medium px-4 py-3">Timezone</th>
                <th className="text-left font-medium px-4 py-3">Custom domain</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-3">
              {tenants.map((t) => (
                <tr key={t.id} className="hover:bg-surface-3 transition">
                  <td className="px-4 py-3">
                    <Link to={`/tenants/${t.id}`} className="text-ink hover:underline underline-offset-4">
                      {t.display_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-muted">{t.slug}</td>
                  <td className="px-4 py-3"><LifecycleBadge state={t.lifecycle_state} /></td>
                  <td className="px-4 py-3"><BillingBadge status={t.billing_status} /></td>
                  <td className="px-4 py-3 text-ink-muted">{t.timezone}</td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                    {t.custom_domain ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
